import { useMemo } from 'react';

import type { SixDetailActivityItemModel } from '@/components/organism/SixDetail/SixDetailActivityPanel/SixDetailActivityPanel';
import { MIN_LEN } from '@/constants';
import type { FolderBreadcrumbItem } from '@/types/SixDetail.types';
import { ACTIVITY_DATE_FORMAT } from '@/utils/sixDetail.utils';

interface ActivityModel {
  id: string;
  description: string;
  userName: string;
  timestamp: Date;
  action: string;
}

export function useActivityCards(activities: ActivityModel[]): SixDetailActivityItemModel[] {
  return useMemo<SixDetailActivityItemModel[]>(() => {
    if (activities.length === MIN_LEN) return [];

    return activities.map((activityItem) => {
      const timestampLabel = Number.isNaN(activityItem.timestamp.getTime())
        ? '-'
        : activityItem.timestamp.toLocaleString('en-US', ACTIVITY_DATE_FORMAT);

      return {
        id: activityItem.id,
        title: activityItem.description,
        actorLabel: `by ${activityItem.userName}`,
        timestampLabel,
        badgeLabel: activityItem.action,
      };
    });
  }, [activities]);
}

export function useBreadcrumbItems(
  folderBreadcrumb: FolderBreadcrumbItem[],
  handleNavigateToRoot: () => void,
  handleBreadcrumbNavigate: (path: string) => void,
): Array<FolderBreadcrumbItem & { onClick?: () => void }> {
  return useMemo<Array<FolderBreadcrumbItem & { onClick?: () => void }>>(
    () => folderBreadcrumb.map((item: FolderBreadcrumbItem, index) => {
      const isLast = index === folderBreadcrumb.length - 1;

      if (isLast) {
        return item;
      }

      if (item.path === undefined) {
        return {
          ...item,
          onClick: handleNavigateToRoot,
        };
      }

      return {
        ...item,
        onClick: () => {
          handleBreadcrumbNavigate(item.id);
        },
      };
    }),
    [folderBreadcrumb, handleNavigateToRoot, handleBreadcrumbNavigate],
  );
}
